import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  Container,
  LinearProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  Publish as PublishIcon,
  Warning as WarningIcon,
  Videocam,
  Info as InfoIcon,
  Delete,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  LockOpen,
  DescriptionRounded,
  GroupsRounded,
  FlagRounded,
  FolderRounded,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useOffice } from '../../store/officeContext';
import { supabase } from '../../supabaseClient';
import { useNavigate, useParams } from 'react-router-dom';
import EditCaseModal from '../../components/Modal/EditCaseModal';
import ChatModal from '../../components/Modal/ChatModal';
import { uploadDocument } from '../../constants/CommonFunctions';
import TasksSection from '../../components/Section/TaskSection';
import AppointmentModal from '../../components/Modal/AppointmentModal';
import CaseInfoModal from '../../components/Modal/CaseInfoModal';

// Importar las traducciones y utilidades
import { formatDate, formatDateTime, formatTime, formatDateWithWeekday, formatDateShort } from '../../constants/DateUtils';
import { useTranslations } from '../../constants/translations/CaseTranslation';
import CaseDocumentSection from '../../components/Section/CaseDocumentSection';
import { tokens } from '../../theme/tokens';
import CaseCopilotDrawer from '../../components/Copilot/CaseCopilotDrawer';
import DraftDocumentModal from '../../components/Copilot/DraftDocumentModal';
import { askCopilot } from '../../hooks/useCopilot';
import { useCachedInsight } from '../../hooks/useCachedInsight';
import AiInsightCard from '../../components/Copilot/AiInsightCard';

// ---- helpers de estilo (mismo lenguaje visual que OfficeListView / TopBar) ----
const AVATAR_GRADIENTS = [
  'linear-gradient(135deg,#6c72ff,#39d3d0)',
  'linear-gradient(135deg,#e5b567,#ff8f6b)',
  'linear-gradient(135deg,#8b5cf6,#ec4899)',
  'linear-gradient(135deg,#39d3d0,#4ad991)',
  'linear-gradient(135deg,#5c7cff,#8b5cf6)',
  'linear-gradient(135deg,#ff6b6b,#e5b567)',
];

const gradientFor = (seed = '') => {
  let hash = 0;
  for (const char of seed) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return AVATAR_GRADIENTS[hash % AVATAR_GRADIENTS.length];
};

const getInitials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase() || '?';

export default function CaseInfoView() {
  const { caseId } = useParams();
  const { office } = useOffice();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);

  const [caseRole, setCaseRole] = useState(null);
  const [openEdit, setOpenEdit] = useState(false);
  const [openPublishModal, setOpenPublishModal] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [publishingCase, setPublishingCase] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);

  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [caseData, setCaseData] = useState(null);

  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [openAppointmentModal, setOpenAppointmentModal] = useState(false);
  const [currentAppointment, setCurrentAppointment] = useState(null);
  const [loadingAppointment, setLoadingAppointment] = useState(false);

  const [documents, setDocuments] = useState([]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [deletingCase, setDeletingCase] = useState(false);

  const [confirmationChecked, setConfirmationChecked] = useState(false);

  const [openReopenModal, setOpenReopenModal] = useState(false);
  const [reopeningCase, setReopeningCase] = useState(false);

  const [caseMembers, setCaseMembers] = useState([]); // Miembros del expediente
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [loadingRole, setLoadingRole] = useState(true);

  const [syncingPermissions, setSyncingPermissions] = useState(false);

  // ---- pestaña activa (nuevo layout) ----
  const [activeTab, setActiveTab] = useState('resumen');

  // Estado unificado para snackbars
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const [openCopilot, setOpenCopilot] = useState(false);
  const [openDraft, setOpenDraft] = useState(false);

  // Función auxiliar para mostrar notificaciones
  const showNotification = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const isCaseReadOnly = () => {
    return caseData?.state === 'completed' || !caseData?.is_visible;
  };

  // Obtener traducciones basadas en el idioma del caso
  const t = useTranslations(caseData?.language);

  const handleAppointmentChange = (appointment) => {
    setCurrentAppointment(appointment);
    loadCurrentAppointment();
  };

  const syncCaseMemberPermissions = async () => {
    try {
      setSyncingPermissions(true);

      await supabase.functions.invoke('sync-member-permissions', {
        body: {
          case_id: caseId
        }
      });

      showNotification(
        'Los permisos del expediente se están sincronizando. Puede tardar unos minutos en reflejarse.',
        'info'
      );
    } catch (error) {
      console.error('Error syncing case permissions:', error);
      showNotification('Error al sincronizar permisos del expediente', 'error');
    } finally {
      setSyncingPermissions(false);
    }
  };

  const loadCurrentAppointment = async () => {
    setLoadingAppointment(true);
    try {
      const now = new Date().toISOString();

      const { data: currentData, error: currentError } = await supabase
        .from('appointment')
        .select('*')
        .eq('case_id', caseId)
        .eq('status', 'scheduled')
        .gte('scheduled_date', now)
        .order('scheduled_date', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (currentError && currentError.code !== 'PGRST116') {
        console.error('Error loading current appointment:', currentError);
        showNotification('Error al cargar la cita actual', 'error');
      } else {
        setCurrentAppointment(currentData);
      }

      const { data: historyData, error: historyError } = await supabase
        .from('appointment')
        .select('*')
        .eq('case_id', caseId)
        .order('scheduled_date', { ascending: false });

      if (historyError) {
        console.error('Error loading appointment history:', historyError);
        showNotification('Error al cargar el historial de citas', 'error');
      } else {
        setAppointmentHistory(historyData || []);
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      showNotification('Error al cargar las citas', 'error');
    } finally {
      setLoadingAppointment(false);
    }
  };

  const loadDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('document')
        .select(`
          *,
          uploader:person!created_by(full_name)
        `)
        .eq('case_id', caseId)
        .order('name', { ascending: true });

      if (error) {
        console.error('Error cargando documentos:', error);
        showNotification('Error al cargar los documentos', 'error');
        return;
      }

      setDocuments(data);
    } catch (error) {
      console.error('Error cargando documentos:', error);
      showNotification('Error al cargar los documentos', 'error');
    }
  };

  useEffect(() => {
    const init = async () => {
      const loadedCase = await loadCase(caseId);
      if (!loadedCase) return;

      await Promise.all([
        loadTasks(caseId),
        loadCaseMembers(caseId),
      ]);

      const role = await loadCaseRole();

      if ((!loadedCase.is_visible || loadedCase.state === 'draft') && role === 'client') {
        navigate(`/office/${office.id}/home`);
        return;
      }

      await Promise.all([
        loadDocuments(),
        loadCurrentAppointment(),
      ]);
    };

    init();
  }, [caseId]);

  const loadCaseRole = async () => {
    setLoadingRole(true);
    try {
      const user = (await supabase.auth.getUser()).data.user;
      if (!user) {
        showNotification('Usuario no autenticado', 'error');
        return null;
      }

      setCurrentUser(user);

      const {data, error} = await supabase
        .from('office_member')
        .select('role')
        .eq('member_id', user.id)
        .eq('office_id', office.id)
        .maybeSingle();

      if (error) {
        console.error('Error cargando el rol del usuario en el expediente: ', error);
        showNotification('Error al cargar permisos de usuario', 'error');
        return null;
      }

      let role = '';

      if(data?.role === 'admin') {
        role = 'admin';
      }
      else {
        const {data: memberData, error} = await supabase
          .from('case_member')
          .select('role')
          .eq('person_id', user.id)
          .eq('case_id', caseId)
          .maybeSingle();

        role = memberData?.role ?? null;
      }

      setCaseRole(role);
      return role;
    } catch (error) {
      console.error('Error en loadCaseRole:', error);
      showNotification('Error al verificar permisos', 'error');
    } finally {
      setLoadingRole(false);
    }
  }

  const loadCase = async (id) => {
    try {
      const { data, error } = await supabase
        .from('case')
        .select(`
          *,
          hierarchy_form:hierarchy_form_id (
            id,
            name,
            hierarchy:hierarchy_id (
              id,
              practice_area_id,
              practice_area (
                id,
                name
              )
            )
          ),
          case_type:case_type_id (
            id,
            name
          ),
          office_status:office_status_id (
            id,
            name,
            form:hierarchy_form!form_id (
              id,
              name
            )
          )
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) {
        console.error('Error loading case:', error);
        showNotification('Error al cargar el expediente', 'error');
        return null;
      }

      if (data?.hierarchy_form?.hierarchy) {
        const hierarchyId = data.hierarchy_form.hierarchy.id;
        const { data: officeHierarchy } = await supabase
          .from('office_hierarchy')
          .select('id')
          .eq('office_id', office.id)
          .eq('hierarchy_id', hierarchyId)
          .maybeSingle();

        if (!officeHierarchy) {
          console.warn('Jerarquía no pertenece al office');
        }
      }

      // Transformar datos para facilitar acceso en el modal
      if (data) {
        const transformedData = {
          ...data,
          // IDs directos para los selects
          practice_area_id: data.hierarchy_form?.hierarchy?.practice_area?.id || null,
          hierarchy_form_id: data.office_status?.form?.id || null,
          case_type_id: data.case_type?.id || null,
          office_status_id: data.office_status_id,
          hierarchy_id: data.office_status?.form?.hierarchy?.id || null
        };

        setCaseData(transformedData);
        return transformedData;
      } else {
        setCaseData(data);
        return data;
      }
    } catch (error) {
      console.error('Error loading case:', error);
      showNotification('Error al cargar el expediente', 'error');
    }
  };

  const loadCaseMembers = async (caseId) => {
    setLoadingMembers(true);
    try {
      const { data, error } = await supabase
        .from('case_member')
        .select(`
          id,
          person_id,
          name_str,
          role,
          person:person_id (
            id,
            full_name,
            email,
            document_number,
            phone_number
          )
        `)
        .eq('case_id', caseId);

      if (error) {
        console.error('Error loading case members:', error);
        showNotification('Error al cargar miembros del expediente', 'error');
        return;
      }

      // Transformar datos
      const transformedMembers = (data || []).map(member => ({
        id: member.id,
        person_id: member.person_id,
        name_str: member.name_str,
        role: member.role,
        full_name: member.person_id ? member.person.full_name : member.name_str,
        email: member.person_id ? member.person.email : null,
        document_number: member.person_id ? member.person.document_number : null,
        phone_number: member.person_id ? member.person.phone_number : null,
      }));

      setCaseMembers(transformedMembers);
    } catch (error) {
      console.error('Error loading case members:', error);
      showNotification('Error al cargar miembros del expediente', 'error');
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadTasks = async (id) => {
    try {
      const { data, error } = await supabase
        .from('task')
        .select('*')
        .eq('case_id', id)
        .order('created_at');

      if (error) {
        console.error('Error loading tasks:', error);
        showNotification('Error al cargar las tareas', 'error');
        return;
      }

      setTasks(data || []);

      // CALCULAR Y RETORNAR EL PROGRESO
      if (data && data.length > 0) {
        const completedCount = data.filter(t => t.completed).length;
        const progress = Math.round((completedCount / data.length) * 100);
        setCaseData(prev => ({ ...prev, progress }));
        return progress;
      } else {
        setCaseData(prev => ({ ...prev, progress: 0 }));
        return 0;
      }
    } catch (error) {
      console.error('Error loading tasks:', error);
      showNotification('Error al cargar las tareas', 'error');
      return 0;
    } finally {
      setLoading(false);
    }
  };

  const recalculateProgress = (updatedTasks) => {
    if (updatedTasks && updatedTasks.length > 0) {
      const completedCount = updatedTasks.filter(t => t.completed).length;
      const progress = Math.round((completedCount / updatedTasks.length) * 100);
      setCaseData(prev => ({ ...prev, progress }));
    } else {
      setCaseData(prev => ({ ...prev, progress: 0 }));
    }
  };

  const handleDeleteCase = async () => {
    if (!caseData) return;

    setDeletingCase(true);
    setConfirmationChecked(false);

    try {
      const { error } = await supabase
        .from('case')
        .delete()
        .eq('id', caseId);

      if (error) {
        console.error('Error deleting case:', error);
        showNotification(t.errorDeletingCase || 'Error al eliminar el expediente', 'error');
        return;
      }

      // Mostrar mensaje de éxito y redirigir
      showNotification(t.caseDeletedSuccessfully || 'Expediente eliminado exitosamente', 'success');
      setOpenDeleteModal(false);

      // Redirigir después de un breve delay para que se vea el mensaje
      setTimeout(() => {
        navigate(`/office/${office.id}/home`);
      }, 1500);

    } catch (error) {
      console.error('Error deleting case:', error);
      showNotification(t.errorDeletingCase || 'Error al eliminar el expediente', 'error');
    } finally {
      setDeletingCase(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handlePublishCase = async () => {
    if (!caseData) return;

    setPublishingCase(true);
    setConfirmationChecked(false);

    let newState;
    switch (caseData.state) {
      case 'draft':
        newState = 'initiated';
        break;
      case 'initiated':
        newState = 'processing';
        break;
      case 'processing':
        newState = 'completed';
        break;
      default:
        newState = 'processing';
    }

    try {
      const { error } = await supabase
        .from('case')
        .update({ state: newState })
        .eq('id', caseId);

      if (error) {
        console.error('Error updating case:', error);
        showNotification('Error al actualizar el expediente', 'error');
        return;
      }

      // Llamar a la edge function solo cuando se cambia de draft a "initiated"
      if (caseData.state === 'draft' && newState === 'initiated') {
        try {
          await supabase.functions.invoke('case-start-notify', {
            body: { case_id: caseId }
          });
          showNotification('Expediente publicado y notificación enviada exitosamente', 'success');
        } catch (emailErr) {
          console.error('Error invocando función de email:', emailErr);
          showNotification('Expediente actualizado, pero hubo un error enviando la notificación por email', 'warning');
        }
      } else {
        const messages = {
          'initiated': 'Expediente publicado exitosamente',
          'processing': 'Expediente iniciado exitosamente',
          'completed': 'Expediente completado exitosamente'
        };
        showNotification(messages[newState] || 'Expediente actualizado exitosamente', 'success');
      }

      setCaseData(prev => ({ ...prev, state: newState }));
      setOpenPublishModal(false);
    } catch (error) {
      console.error('Error updating case:', error);
      showNotification('Error al actualizar el expediente', 'error');
    } finally {
      setPublishingCase(false);
    }
  };

  const handleUploadDocument = async (file) => {
    if (!file || !office?.id || !caseData?.id || !currentUser?.id) {
      throw new Error('Faltan datos requeridos para la subida');
    }

    try {
      const uploaded = await uploadDocument({
        file,
        officeId: office.id,
        caseId: caseData.id,
        uploadedBy: currentUser.id
      });

      await loadDocuments();
      showNotification('Documento subido exitosamente', 'success');
      return uploaded;
    } catch (err) {
      console.error('Error al subir documento:', err);
      showNotification('Error al subir el documento', 'error');
      throw err;
    }
  };

  const getStatusColor = (state, isVisible = true) => {
    // Si no es visible, usar color rojo para indicar eliminado
    if (!isVisible) {
      return '#d32f2f'; // Rojo para eliminado
    }

    switch (state) {
      case 'processing': return office?.color || '#1976d2';
      case 'completed': return '#2e7d32';
      case 'draft': return '#9e9e9e';
      case 'initiated': return office?.color || '#1976d2';
      default: return office?.color || '#1976d2';
    }
  };

  const getStatusLabel = (state, isVisible = true) => {
    // Si no es visible, mostrar como eliminado independientemente del estado
    if (!isVisible) {
      return t.statusDeleted || 'Eliminado';
    }

    switch (state) {
      case 'draft': return t.statusDraft;
      case 'initiated': return t.statusInitiated;
      case 'processing': return t.statusProcessing;
      case 'completed': return t.statusCompleted;
      default: return state;
    }
  };

  const handleReopenCase = async () => {
    if (!caseData) return;

    setReopeningCase(true);
    setConfirmationChecked(false);

    try {
      const { error } = await supabase
        .from('case')
        .update({ state: 'processing' })
        .eq('id', caseId);

      if (error) {
        console.error('Error reopening case:', error);
        showNotification(t.errorReopeningCase || 'Error al reabrir el expediente', 'error');
        return;
      }

      showNotification(t.caseReopenedSuccessfully || 'Expediente reabierto exitosamente', 'success');
      setCaseData(prev => ({ ...prev, state: 'processing' }));
      setOpenReopenModal(false);
    } catch (error) {
      console.error('Error reopening case:', error);
      showNotification(t.errorReopeningCase || 'Error al reabrir el expediente', 'error');
    } finally {
      setReopeningCase(false);
    }
  };

  const handleCloseReopenModal = () => {
    setConfirmationChecked(false);
    setOpenReopenModal(false);
  };

  // Función para manejar la actualización del caso desde EditCaseModal
  const handleCaseUpdate = async (formValues) => {
    try {
      const { error } = await supabase
        .from('case')
        .update({
          title: formValues.title,
          description: formValues.description,
          language: formValues.language,
          hierarchy_form_id: formValues.hierarchy_form_id,
          case_type_id: formValues.case_type_id || null,
          office_status_id: formValues.office_status_id,
          start_date: formValues.start_date || null,
          end_date: formValues.end_date || null,
        })
        .eq('id', caseData.id);

      if (error) {
        console.error('Error al guardar cambios:', error);
        showNotification('Error al guardar los cambios', 'error');
      } else {
        await loadCase(caseId);
        await loadCaseMembers(caseId);
        recalculateProgress(tasks);
        showNotification('Expediente actualizado exitosamente', 'success');
        setOpenEdit(false);
      }
    } catch (error) {
      console.error('Error al guardar cambios:', error);
      showNotification('Error al guardar los cambios', 'error');
    }
  };

  const clientMember = useMemo(() => {
    return caseMembers.find(m => m.role === 'client') || null;
  }, [caseMembers]);

  const lawyerMember = useMemo(() => {
    return caseMembers.find(m => m.role === 'lawyer') || null;
  }, [caseMembers]);

  const partnerMembers = useMemo(() => {
    return caseMembers.filter(m => m.role === 'partner') || [];
  }, [caseMembers]);

  const timelineEvents = useMemo(() => {
    if (!caseData) return [];
    const events = [];

    events.push({
      date: caseData.created_at,
      icon: 'created',
      title: 'Expediente creado',
      subtitle: caseData.number || '',
    });

    tasks
      .filter((task) => task.completed && task.finish_date)
      .forEach((task) => {
        events.push({
          date: task.finish_date,
          icon: 'task',
          title: `Tarea completada: ${task.text}`,
          subtitle: null,
        });
      });

    documents.forEach((doc) => {
      events.push({
        date: doc.created_at,
        icon: 'document',
        title: `Documento subido: ${doc.name || doc.filename || 'Sin nombre'}`,
        subtitle: doc.uploader?.full_name || null,
      });
    });

    appointmentHistory.forEach((apt) => {
      events.push({
        date: apt.created_at,
        icon: 'appointment',
        title: `Cita agendada · ${formatDateShort(apt.scheduled_date, caseData?.language)} ${formatTime(apt.scheduled_date, caseData?.language)}`,
        subtitle: getStatusLabel(apt.status),
      });
    });

    return events
      .filter((e) => !!e.date)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [caseData, tasks, documents, appointmentHistory]);

  const timelineIcon = (type) => {
    const iconProps = { sx: { fontSize: 16 } };
    switch (type) {
      case 'task': return <CheckCircleIcon {...iconProps} />;
      case 'document': return <DescriptionRounded {...iconProps} />;
      case 'appointment': return <Videocam {...iconProps} />;
      default: return <FlagRounded {...iconProps} />;
    }
  };

  const pendingTasks = useMemo(() => tasks.filter((task) => !task.completed), [tasks]);

  const breatheAnimation = {
    '@keyframes breathe': {
      '0%, 100%': {
        boxShadow: '0 0 5px rgba(76, 175, 80, 0.5)',
      },
      '50%': {
        boxShadow: '0 0 20px rgba(76, 175, 80, 0.8), 0 0 30px rgba(76, 175, 80, 0.6)',
      },
    },
    animation: 'breathe 2s ease-in-out infinite',
  };

  const insight = useCachedInsight(
    caseData?.id ? `insight:case:${caseData.id}` : null,
    () => askCopilot(caseId, [{ role: 'user', content: 'Dame un resumen ejecutivo breve (máximo 3 líneas) del estado actual de este expediente y cuál es el próximo paso a seguir.' }]),
    !!caseData
  );

  if (loading || loadingRole) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!caseData) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="error">
            {t.caseNotFound}
          </Typography>
          <Button
            onClick={() => navigate(`/office/${office.id}/home`)}
            sx={{ mt: 2, color: office?.color }}
          >
            {t.backToCases}
          </Button>
        </Box>
      </Container>
    );
  }

  const practiceAreaName = caseData?.hierarchy_form?.hierarchy?.practice_area?.name;
  const caseTypeName = caseData?.case_type?.name;

  // Botones del header con el mismo lenguaje visual que el mockup (pill, sin mayúsculas)
  const heroBtnSx = { textTransform: 'none', fontWeight: 650, borderRadius: '12px', fontSize: 13.5 };

  const TABS = [
    { key: 'resumen', label: 'Resumen' },
    { key: 'tareas', label: 'Tareas', count: tasks.length },
    { key: 'documentos', label: 'Documentos', count: documents.length },
    { key: 'otros', label: 'Otros' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 2 }}>
      <Button
        onClick={() =>
          caseData?.state === 'completed'
            ? navigate('/archived-cases')
            : navigate(`/office/${office.id}/home`)
        }
        disableRipple
        sx={{
          mb: 1.5,
          px: 0,
          minWidth: 0,
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          color: tokens.colors.muted,
          '&:hover': { color: office?.color, backgroundColor: 'transparent' },
        }}
      >
        {t.backToCases}
      </Button>

      {/* ---- HERO ---- */}
      <Paper sx={{ borderRadius: `${tokens.radius.lg}px`, p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: { xs: 22, sm: 25 }, fontWeight: 730, letterSpacing: '-0.4px', wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                {caseData.title}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.875,
                  fontSize: 12.5,
                  fontWeight: 650,
                  padding: '6px 12px',
                  borderRadius: '20px',
                  backgroundColor: `${getStatusColor(caseData.state, caseData.is_visible)}1F`,
                  color: getStatusColor(caseData.state, caseData.is_visible),
                }}
              >
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: 'currentColor' }} />
                {getStatusLabel(caseData.state, caseData.is_visible)}
              </Box>
              {isCaseReadOnly() && (
                <Chip
                  label={caseData?.state === 'completed' ? t.readOnly : t.hidden}
                  size="small"
                  sx={{
                    backgroundColor: caseData?.state === 'completed' ? '#2e7d32' : '#d32f2f',
                    color: 'white'
                  }}
                />
              )}
              <Tooltip title={t.viewCaseInfo}>
                <IconButton
                  size="small"
                  onClick={() => setOpenInfoModal(true)}
                  sx={{ color: office?.color, '&:hover': { backgroundColor: `${office?.color}20` } }}
                >
                  <InfoIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ color: tokens.colors.muted, fontSize: 13.5, mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <FolderRounded sx={{ fontSize: 15, opacity: 0.7 }} />
                {t.caseNumber}: {caseData.number}
              </Box>
              {(caseTypeName || practiceAreaName) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <ScheduleIcon sx={{ fontSize: 15, opacity: 0.7 }} />
                  {[practiceAreaName, caseTypeName].filter(Boolean).join(' · ')}
                </Box>
              )}
              {caseData.start_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <CalendarIcon sx={{ fontSize: 15, opacity: 0.7 }} />
                  {t.start}: {formatDate(caseData.start_date, caseData?.language)}
                </Box>
              )}
              {caseMembers.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <GroupsRounded sx={{ fontSize: 15, opacity: 0.7 }} />
                  {[lawyerMember?.full_name, ...partnerMembers.map((p) => p.full_name)]
                    .filter(Boolean)
                    .join(' · ') || t.notAssigned}
                </Box>
              )}
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              startIcon={<AutoAwesomeIcon />}
              onClick={() => setOpenCopilot(true)}
              sx={{
                ...heroBtnSx,
                backgroundImage: tokens.gradient.main,
                color: '#08131a',
                '&:hover': { backgroundImage: tokens.gradient.main, filter: 'brightness(1.06)' },
              }}
            >
              Preguntar a la IA
            </Button>
            {caseRole !== 'client' &&
              !isCaseReadOnly() &&
              (caseData.state === 'draft' || caseData.state === 'initiated' || caseData.state === 'processing') && (
                <Button
                  variant="contained"
                  startIcon={<PublishIcon />}
                  onClick={() => setOpenPublishModal(true)}
                  sx={{
                    ...heroBtnSx,
                    backgroundColor: caseData.state === 'processing' ? '#2e7d32' : '#4caf50',
                    '&:hover': {
                      backgroundColor: caseData.state === 'processing' ? '#1b5e20' : '#45a049'
                    }
                  }}
                >
                  {caseData.state === 'draft' ? t.publish :
                  caseData.state === 'initiated' ? t.startCase :
                  t.completeCase}
                </Button>
              )}

            {caseRole === 'admin' &&
              caseData?.state === 'completed' &&
              caseData?.is_visible && (
                <Button
                  variant="outlined"
                  startIcon={<LockOpen />}
                  onClick={() => setOpenReopenModal(true)}
                  sx={{
                    ...heroBtnSx,
                    borderColor: office?.color,
                    color: office?.color,
                    '&:hover': {
                      backgroundColor: '#fff3e0',
                      borderColor: office?.color
                    }
                  }}
                >
                  {t.reopenCase || 'Reabrir'}
                </Button>
              )}

            {caseRole &&
              caseRole !== 'client' &&
              !isCaseReadOnly() && (
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  sx={{ ...heroBtnSx, borderColor: office?.color, color: office?.color }}
                  onClick={() => setOpenEdit(true)}
                >
                  {t.edit}
                </Button>
              )}

            {caseRole &&
              caseRole !== 'client' &&
              caseData?.is_visible &&
              caseData.state !== 'completed' && (
                <Button
                  variant="contained"
                  startIcon={<Delete />}
                  onClick={() => setOpenDeleteModal(true)}
                  sx={{
                    ...heroBtnSx,
                    backgroundColor: '#d32f2f',
                    '&:hover': { backgroundColor: '#b71c1c' }
                  }}
                >
                  {t.delete}
                </Button>
              )}
          </Box>
        </Box>

        {!caseData.is_visible && caseRole !== 'client' && (
          <Box sx={{ mt: 2, p: 2, backgroundColor: '#ffebee', border: '1px solid #f44336', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
              ⚠️ {t.caseHiddenWarning || 'Este expediente está oculto para el cliente'}
            </Typography>
          </Box>
        )}

        {isCaseReadOnly() && (
          <Box sx={{
            mt: 2,
            p: 2,
            backgroundColor: caseData?.state === 'completed' ? '#e8f5e8' : '#ffebee',
            border: `1px solid ${caseData?.state === 'completed' ? '#4caf50' : '#f44336'}`,
            borderRadius: 1
          }}>
            <Typography variant="body2" sx={{
              color: caseData?.state === 'completed' ? '#2e7d32' : '#d32f2f',
              fontWeight: 'bold'
            }}>
              {caseData?.state === 'completed'
                ? `✅ ${t.caseCompletedInfo || 'Este expediente ha sido completado y es solo de consulta'}`
                : `⚠️ ${t.caseHiddenWarning || 'Este expediente está oculto para el cliente'}`
              }
            </Typography>
          </Box>
        )}

        {caseRole && caseRole !== 'client' && (
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={syncCaseMemberPermissions}
              disabled={syncingPermissions}
              sx={{ ...heroBtnSx, borderColor: office?.color, color: office?.color }}
            >
              {syncingPermissions ? 'Sincronizando...' : 'Sincronizar permisos'}
            </Button>
          </Box>
        )}

        {/* stats */}
        <Grid container spacing={2} sx={{ mt: 1.5, pt: 2, borderTop: `1px solid ${tokens.colors.line}` }}>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Tareas</Typography>
            <Typography variant="h6" fontWeight={720}>{tasks.filter(tk => tk.completed).length} / {tasks.length}</Typography>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Documentos</Typography>
            <Typography variant="h6" fontWeight={720}>{documents.length}</Typography>
          </Grid>
          <Grid item size={{ xs: 6, sm: 4 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Próxima cita</Typography>
            <Typography variant="h6" fontWeight={720} sx={{ color: currentAppointment ? office?.color : 'text.secondary' }}>
              {currentAppointment ? formatDateShort(currentAppointment.scheduled_date, caseData?.language) : 'Sin cita'}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ mt: 2 }}>
          <LinearProgress
            variant="determinate"
            value={caseData.progress || 0}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': { backgroundColor: office?.color }
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {t.caseProgress || 'Progreso del expediente'}
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {caseData.progress || 0}% {t.completed || 'completado'}
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* ---- TABS ---- */}
      <Tabs
        value={activeTab}
        onChange={(e, value) => setActiveTab(value)}
        sx={{ mb: 3, borderBottom: `1px solid ${tokens.colors.line}` }}
        variant="scrollable"
        scrollButtons="auto"
      >
        {TABS.map((tabItem) => (
          <Tab
            key={tabItem.key}
            value={tabItem.key}
            label={typeof tabItem.count === 'number' ? `${tabItem.label} (${tabItem.count})` : tabItem.label}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          />
        ))}
      </Tabs>

      {/* ---- RESUMEN ---- */}
      {activeTab === 'resumen' && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 8 }}>
            <AiInsightCard loading={insight.loading} text={insight.text} error={insight.error} label="Resumen automático del expediente" />

            <Paper sx={{ p: 3, mb: 3, borderRadius: `${tokens.radius.lg}px` }}>
              <Typography variant="h6" gutterBottom>Datos del expediente</Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  columnGap: 5,
                }}
              >
                {[
                  ['Tipo de expediente', caseTypeName || '—'],
                  ['Área de práctica', practiceAreaName || '—'],
                  [t.caseNumber, caseData.number],
                  ['Idioma', caseData.language || '—'],
                  ['Fecha de inicio', caseData.start_date ? formatDate(caseData.start_date, caseData?.language) : '—'],
                  ['Fecha de fin', caseData.end_date ? formatDate(caseData.end_date, caseData?.language) : '—'],
                ].map(([label, value]) => (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      gap: 2,
                      py: 1.375,
                      borderBottom: `1px solid ${tokens.colors.line}`,
                      fontSize: 13.5,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{label}</Typography>
                    <Typography variant="body2" fontWeight={600} textAlign="right" sx={{ wordBreak: 'break-word' }}>{value}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: `${tokens.radius.lg}px` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ flex: 1 }}>Tareas pendientes</Typography>
                <Button size="small" onClick={() => setActiveTab('tareas')} sx={{ color: tokens.colors.cyan, fontWeight: 700 }}>
                  Ver todas
                </Button>
              </Box>
              {pendingTasks.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No hay tareas pendientes.</Typography>
              ) : (
                <List dense>
                  {pendingTasks.slice(0, 5).map((task) => (
                    <ListItem key={task.id} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <ScheduleIcon fontSize="small" sx={{ color: office?.color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={task.text}
                        secondary={task.due_date ? `Vence: ${formatDate(task.due_date, caseData?.language)}` : null}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 3, mb: 3, borderRadius: `${tokens.radius.lg}px` }}>
              <Typography variant="h6" gutterBottom>{t.contactInfo}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t.lawyerData}</Typography>

              {loadingMembers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <>
                  {lawyerMember ? (
                    <>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ mr: 2, backgroundColor: office?.color }}>
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {lawyerMember.full_name.replace(';', '')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t.assignedLawyer}
                          </Typography>
                        </Box>
                      </Box>

                      {lawyerMember.phone_number && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <PhoneIcon sx={{ mr: 1, color: office?.color }} />
                          <Typography variant="body2">{lawyerMember.phone_number}</Typography>
                        </Box>
                      )}

                      {lawyerMember.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <EmailIcon sx={{ mr: 1, color: office?.color }} />
                          <Typography variant="body2">{lawyerMember.email}</Typography>
                        </Box>
                      )}
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ mr: 2, backgroundColor: 'grey.400' }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="body1" fontWeight="bold">{t.notAssigned}</Typography>
                        <Typography variant="body2" color="text.secondary">{t.assignedLawyer}</Typography>
                      </Box>
                    </Box>
                  )}

                  {partnerMembers.length > 0 && (
                    <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        {t.attorneys || 'Colaboradores'}
                      </Typography>
                      {partnerMembers.map((partner, index) => (
                        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Avatar sx={{ mr: 1, backgroundColor: 'grey.600', width: 32, height: 32 }}>
                            <PersonIcon fontSize="small" />
                          </Avatar>
                          <Typography variant="body2">{partner.full_name.replace(';', '')}</Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </>
              )}

              {!isCaseReadOnly() && (
                <Button
                  variant="outlined"
                  fullWidth
                  sx={{ mt: 1, borderColor: office?.color, color: office?.color }}
                  onClick={() => setOpenChat(true)}
                >
                  {t.openChat}
                </Button>
              )}
            </Paper>

            <Paper sx={{ p: 3, borderRadius: `${tokens.radius.lg}px` }}>
              <Typography variant="h6" gutterBottom>{t.nextSteps}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t.pendingActions}</Typography>

              <Button
                fullWidth
                variant={currentAppointment ? "outlined" : "contained"}
                startIcon={loadingAppointment ? <CircularProgress size={16} /> : <Videocam />}
                onClick={() => setOpenAppointmentModal(true)}
                disabled={loadingAppointment || isCaseReadOnly()}
                sx={currentAppointment ?
                  { mb: 2, borderColor: office?.color, color: office?.color } :
                  {
                    mb: 2,
                    backgroundColor: '#4caf50',
                    '&:hover': { backgroundColor: '#45a049' },
                    ...(!isCaseReadOnly() ? breatheAnimation : {})
                  }
                }
              >
                {loadingAppointment ? t.loading :
                currentAppointment ? t.manageAppointment : t.scheduleAppointment}
              </Button>

              {currentAppointment && (
                <Box sx={{ mb: 2, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Videocam sx={{ mr: 1, color: '#4caf50' }} />
                    <Typography variant="body2" fontWeight="bold">{t.nextAppointment}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                    {formatDateTime(currentAppointment.scheduled_date, caseData?.language)}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* ---- PARTES ---- */}
      {/* ---- OTROS (Partes, Citas, Cronología) ---- */}
      {activeTab === 'otros' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Paper sx={{ p: 3, borderRadius: `${tokens.radius.lg}px` }}>
          <Typography variant="h6" gutterBottom>Partes e intervinientes</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Personas vinculadas a este expediente
          </Typography>
          {loadingMembers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : caseMembers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No hay partes registradas todavía.</Typography>
          ) : (
            <List>
              {caseMembers.map((member) => (
                <ListItem key={member.id} sx={{ px: 0, borderBottom: `1px solid ${tokens.colors.line}`, '&:last-child': { borderBottom: 'none' } }}>
                  <ListItemIcon>
                    <Avatar sx={{ background: gradientFor(member.full_name || ''), color: '#08131a', fontWeight: 700, fontSize: 13 }}>
                      {getInitials(member.full_name)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={member.full_name?.replace(';', '')}
                    secondary={[member.email, member.phone_number].filter(Boolean).join(' · ')}
                  />
                  <Chip
                    size="small"
                    label={member.role === 'client' ? 'Cliente' : member.role === 'lawyer' ? 'Abogado' : 'Colaborador'}
                    sx={{ backgroundColor: tokens.colors.panel2, color: tokens.colors.muted, fontWeight: 650 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>

        <Paper sx={{ p: 3, borderRadius: `${tokens.radius.lg}px` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="h6">{t.appointmentHistory}</Typography>
              <Typography variant="body2" color="text.secondary">{t.appointmentHistoryDesc}</Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Videocam />}
              onClick={() => setOpenAppointmentModal(true)}
              sx={{ backgroundColor: office?.color, '&:hover': { backgroundColor: office?.color, opacity: 0.9 } }}
            >
              {t.manageAppointments}
            </Button>
          </Box>

          {loadingAppointment ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {currentAppointment && (
                <Box sx={{ mb: 3, mt: 2, p: 2, border: '2px solid #4caf50', borderRadius: 1, backgroundColor: '#e8f5e8' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#2e7d32', display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Videocam sx={{ color: '#4caf50' }} />
                      {t.currentAppointment}
                    </Typography>
                    <Chip label={t.active} size="small" sx={{ backgroundColor: '#4caf50', color: 'white', fontWeight: 'bold' }} />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ScheduleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                      <Typography variant="body2" fontWeight="bold">
                        {formatDateWithWeekday(currentAppointment.scheduled_date, caseData?.language)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                      {t.at} {formatTime(currentAppointment.scheduled_date, caseData?.language)}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<Videocam />}
                    href={currentAppointment.meet_link}
                    target="_blank"
                    sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}
                  >
                    {t.joinMeeting}
                  </Button>
                </Box>
              )}

              {appointmentHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">{t.noAppointmentsYet}</Typography>
              ) : (
                <List>
                  {appointmentHistory
                    .filter(apt => !(currentAppointment && apt.id === currentAppointment.id))
                    .map((appointment) => {
                      const isPast = new Date(appointment.scheduled_date) < new Date();
                      const statusColor = getStatusColor(appointment.status);

                      return (
                        <ListItem
                          key={appointment.id}
                          sx={{
                            mb: 1,
                            border: '1px solid #e0e0e0',
                            borderRadius: 1,
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                        >
                          <ListItemIcon>
                            <Videocam sx={{ color: statusColor }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Typography variant="body1" fontWeight="medium">
                                  {formatDateShort(appointment.scheduled_date, caseData?.language)} - {formatTime(appointment.scheduled_date, caseData?.language)}
                                </Typography>
                                <Chip
                                  label={getStatusLabel(appointment.status)}
                                  size="small"
                                  sx={{ backgroundColor: statusColor, color: 'white', fontSize: '0.75rem' }}
                                />
                                {isPast && appointment.status === 'scheduled' && (
                                  <Chip
                                    label={t.expired}
                                    size="small"
                                    sx={{ backgroundColor: '#ff9800', color: 'white', fontSize: '0.75rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography
                                  variant="body2"
                                  component="a"
                                  href={appointment.meet_link}
                                  target="_blank"
                                  sx={{ color: '#1976d2', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                                >
                                  {t.meetingLink}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {t.scheduledOn} {formatDate(appointment.created_at, caseData?.language)} {t.at} {formatTime(appointment.created_at, caseData?.language)}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      );
                    })}
                </List>
              )}
            </>
          )}
        </Paper>

        <Paper sx={{ p: 3, borderRadius: `${tokens.radius.lg}px` }}>
          <Typography variant="h6" gutterBottom>Cronología del expediente</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Historial de actuaciones (tareas, documentos y citas)
          </Typography>
          {timelineEvents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Todavía no hay actividad registrada.</Typography>
          ) : (
            <Box sx={{ position: 'relative', pl: 4 }}>
              <Box sx={{ position: 'absolute', left: 11, top: 6, bottom: 6, width: '2px', backgroundColor: tokens.colors.line }} />
              {timelineEvents.map((event, idx) => (
                <Box key={idx} sx={{ position: 'relative', pb: idx === timelineEvents.length - 1 ? 0 : 3 }}>
                  <Box sx={{
                    position: 'absolute',
                    left: -32,
                    top: 0,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    backgroundColor: event.icon === 'created' ? office?.color : tokens.colors.panel2,
                    color: event.icon === 'created' ? '#fff' : tokens.colors.muted,
                  }}>
                    {timelineIcon(event.icon)}
                  </Box>
                  <Typography variant="caption" color="text.secondary">
                    {formatDateTime(event.date, caseData?.language)}
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>{event.title}</Typography>
                  {event.subtitle && (
                    <Typography variant="caption" color="text.secondary">{event.subtitle}</Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
        </Box>
      )}

      {/* ---- TAREAS ---- */}
      {activeTab === 'tareas' && (
        <TasksSection
          tasks={tasks}
          setTasks={setTasks}
          caseRole={caseRole}
          office={office}
          caseData={caseData}
          readOnly={isCaseReadOnly()}
          onProgressUpdate={recalculateProgress}
        />
      )}

      {/* ---- DOCUMENTOS ---- */}
      {activeTab === 'documentos' && (
        <>
          {caseRole && caseRole !== 'client' && (
            <Button
              variant="outlined" size="small" startIcon={<AutoAwesomeIcon />}
              onClick={() => setOpenDraft(true)}
              sx={{ mb: 2, borderColor: tokens.colors.indigo, color: tokens.colors.indigo, textTransform: 'none', fontWeight: 650 }}
            >
              Redactar con IA
            </Button>
          )}
          <CaseDocumentSection
            documents={documents}
            onDocumentsChange={loadDocuments}
            caseData={caseData}
            office={office}
            caseRole={caseRole}
            currentUser={currentUser}
            onUploadDocument={handleUploadDocument}
            readOnly={isCaseReadOnly()}
            />
        </>
      )}

      <CaseInfoModal
        open={openInfoModal}
        onClose={() => setOpenInfoModal(false)}
        caseData={{
          ...caseData,
          case_members: caseMembers
        }}
        caseMembers={caseMembers}
        officeColor={office?.color}
      />

      <EditCaseModal
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        caseData={caseData}
        onSave={handleCaseUpdate}
        readOnly={isCaseReadOnly()}
      />

      <ChatModal
        open={openChat}
        onClose={() => setOpenChat(false)}
        caseId={caseId}
        currentUserId={currentUser?.id}
        currentUserRole={caseRole}
        officeColor={office?.color}
        caseData={caseData}
        caseMembers={caseMembers}
        caseLanguage={caseData?.language}
        disabled={!caseData?.is_visible}
      />

      <CaseCopilotDrawer
        open={openCopilot}
        onClose={() => setOpenCopilot(false)}
        caseId={caseId}
      />

      <Dialog
        open={openPublishModal}
        onClose={() => setOpenPublishModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon sx={{ color: '#ff9800' }} />
          {caseData.state === 'draft' ? t.confirmPublication :
          caseData.state === 'initiated' ? t.confirmStart :
          t.confirmCompletion}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {caseData.state === 'draft' ? t.onPublishing :
            caseData.state === 'initiated' ? t.onStarting :
            t.onCompleting}
          </DialogContentText>
          <Box sx={{ mt: 2, mb: 2 }}>
            {caseData.state === 'draft' ?
            <>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.visibleToLawyerClient}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.automaticNotification}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.statusChangeDraftInitiated}
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                {t.cannotUndo}
              </Typography>
            </> :
            caseData.state === 'initiated' ?
            <>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.lawyerCanAddTasks}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.clientCanCompleteTasks}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.chatEnabled}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.statusChangeInitiatedProcessing}
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                {t.cannotUndo}
              </Typography>
            </> :
            <>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.caseWillBeCompleted}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.noMoreEditing}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.readOnlyAccess}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
                {t.statusChangeProcessingCompleted}
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                {t.cannotUndo}
              </Typography>
            </>
            }
          </Box>
          <DialogContentText>
            {caseData.state === 'draft' ? t.confirmPublishQuestion :
            caseData.state === 'initiated' ? t.confirmStartQuestion :
            t.confirmCompleteQuestion} "<strong>{caseData.title}</strong>"?
          </DialogContentText>

          <Grid sx={{ mt: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  sx={{
                    color: office?.color,
                    '&.Mui-checked': { color: office?.color },
                  }}
                />
              }
              label={t.confirmationCheckbox}
              sx={{ alignItems: 'flex-start' }}
            />
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenPublishModal(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handlePublishCase}
            variant="contained"
            disabled={publishingCase || !confirmationChecked}
            startIcon={publishingCase ? <CircularProgress size={16} /> : <PublishIcon />}
            sx={{
              backgroundColor: caseData.state === 'processing' ? '#2e7d32' : '#4caf50',
              '&:hover': {
                backgroundColor: caseData.state === 'processing' ? '#1b5e20' : '#45a049'
              },
              '&:disabled': { backgroundColor: '#cccccc' }
            }}
          >
            {publishingCase ? t.updating :
            caseData.state === 'draft' ? t.publishCase :
            caseData.state === 'initiated' ? t.startCaseAction :
            t.completeCase}
          </Button>
        </DialogActions>
      </Dialog>

      <AppointmentModal
        open={openAppointmentModal}
        onClose={() => setOpenAppointmentModal(false)}
        caseId={caseId}
        officeColor={office?.color}
        onAppointmentScheduled={handleAppointmentChange}
        caseLanguage={caseData?.language}
        readOnly={isCaseReadOnly()}
      />

      {/* Modal de Confirmación de Eliminación */}
      <Dialog
        open={openDeleteModal}
        onClose={() => !deletingCase && setOpenDeleteModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Delete sx={{ color: '#d32f2f' }} />
          {t.confirmDeletion || 'Confirmar Eliminación'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t.deletionWarning || 'Estás a punto de eliminar este expediente. Esta acción:'}
          </DialogContentText>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.willHideFromClient || 'Ocultará el expediente del cliente'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.willDisableAccess || 'Deshabilitará el acceso a todas las funciones'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.willPreserveData || 'Los datos se conservarán en el sistema'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center', color: '#d32f2f', fontWeight: 'bold' }}>
              • {t.cannotUndoAction || 'Esta acción no se puede deshacer fácilmente'}
            </Typography>
          </Box>
          <DialogContentText sx={{ mt: 2 }}>
            {t.confirmDeleteQuestion || '¿Estás seguro de que deseas eliminar'} "<strong>{caseData?.title}</strong>"?
          </DialogContentText>
          <Box sx={{ mt: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  sx={{
                    color: '#d32f2f',
                    '&.Mui-checked': { color: '#d32f2f' }
                  }}
                />
              }
              label={t.confirmationCheckbox}
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenDeleteModal(false)}
            variant="outlined"
            disabled={deletingCase}
            sx={{ mr: 1 }}
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleDeleteCase}
            variant="contained"
            disabled={deletingCase || !confirmationChecked}
            startIcon={deletingCase ? <CircularProgress size={16} /> : <Delete />}
            sx={{
              backgroundColor: '#d32f2f',
              '&:hover': { backgroundColor: '#b71c1c' },
              '&:disabled': { backgroundColor: '#cccccc' }
            }}
          >
            {deletingCase ? (t.deleting || 'Eliminando...') : (t.deleteCase || 'Eliminar Expediente')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openReopenModal}
        onClose={() => setOpenReopenModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockOpen sx={{ color: office?.color }} />
          {t.confirmReopening || 'Confirmar Reapertura'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t.onReopening || 'Al reabrir este expediente:'}
          </DialogContentText>
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.caseWillBeReopened || 'El expediente volverá al estado "En Proceso"'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.editingWillBeEnabled || 'Se habilitará nuevamente la edición'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.tasksCanBeModified || 'Las tareas podrán ser modificadas'}
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, display: 'flex', alignItems: 'center' }}>
              • {t.chatWillBeEnabled || 'El chat se habilitará nuevamente'}
            </Typography>
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', color: office?.color, fontWeight: 'bold' }}>
              • {t.canBeCompletedAgain || 'Podrá volver a ser completado posteriormente'}
            </Typography>
          </Box>
          <DialogContentText>
            {t.confirmReopenQuestion || '¿Estás seguro de que deseas reabrir'} "<strong>{caseData.title}</strong>"?
          </DialogContentText>

          <Box sx={{ mt: 3, mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmationChecked}
                  onChange={(e) => setConfirmationChecked(e.target.checked)}
                  sx={{ color: office?.color }}
                />
              }
              label={t.confirmationCheckbox || 'Confirmo que entiendo las implicaciones de esta acción'}
              sx={{ alignItems: 'flex-start' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setOpenReopenModal(false)}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            {t.cancel}
          </Button>
          <Button
            onClick={handleReopenCase}
            variant="contained"
            disabled={reopeningCase || !confirmationChecked}
            startIcon={reopeningCase ? <CircularProgress size={16} /> : <LockOpen />}
            sx={{
              backgroundColor: office?.color,
              '&:hover': { backgroundColor: office?.color },
            }}
          >
            {reopeningCase ? (t.reopening || 'Reabriendo...') : (t.reopenCase || 'Reabrir Expediente')}
          </Button>
        </DialogActions>
      </Dialog>

      <DraftDocumentModal open={openDraft} onClose={() => setOpenDraft(false)} caseId={caseId} />

      {/* Snackbar unificado para todas las notificaciones */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            '& .MuiAlert-icon': {
              alignItems: 'center'
            }
          }}
          icon={
            snackbar.severity === 'success' ? <CheckCircleIcon /> :
            snackbar.severity === 'error' ? <ErrorIcon /> :
            snackbar.severity === 'warning' ? <WarningIcon /> :
            <InfoIcon />
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Container>
  );
}
